import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, MicrophoneIcon, SparklesIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

import api from '../../api/axiosInstance';
import useAuthStore from '../../store/authStore';
import useAppStore from '../../store/appStore';
import languageList from '../../data/languages.json';
import {
  norm,
  toTitle,
  getTypeIcon,
  getIconBgColor,
  getIconTextColor,
} from '../../lib/commonUtils';

const cutMandatoryTail = (text = '') => {
  if (!text) return '';
  const idx = text.toLowerCase().indexOf('important:');
  return idx >= 0 ? text.slice(0, idx).trim() : text;
};

const extractPrompt = (raw = {}) => {
  const candidates = [
    raw.prompt,
    raw.agent_prompt,
    raw.initial_prompt,
    raw.system_prompt,
    raw.description?.prompt,
    raw.config?.prompt,
    raw.prompts?.system,
    raw.prompts?.initial,
  ].filter(Boolean);

  for (const v of candidates) {
    const str = String(v ?? '').trim();
    if (str) return str;
  }

  for (const [k, v] of Object.entries(raw)) {
    if (/prompt/i.test(k) && typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
};

const normalizeFromAnalytics = (raw = {}) => ({
  id: raw.agent_id ?? String(raw.id ?? raw._id ?? ''),
  name: raw.agent_name ?? raw.name ?? 'Untitled Agent',
  type: norm(raw.agent_type ?? raw.type ?? 'customer_support'),
  language: raw.language ?? '',
  // model: raw.llm ?? '',
  speakingStyle: raw.speaking_style ?? 'professional',
  prompt: extractPrompt(raw) || '',
});

const CheckBadge = () => (
  <span className="absolute right-2 top-2 inline-flex items-center justify-center h-5 w-5 rounded-full bg-primary-600 text-white text-[10px] shadow-sm">
    ✓
  </span>
);

const AgentRetrain = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const { updateAgentExact, agentsScope } = useAppStore();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [agentName, setAgentName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedType, setSelectedType] = useState('customer_support');
  const [language, setLanguage] = useState('English');
  // const [model, setModel] = useState(''); 
  const [speakingStyle, setSpeakingStyle] = useState('professional');
  const [prompt, setPrompt] = useState('');

  const [voiceFile, setVoiceFile] = useState(null);
  const [knowledgeFile, setKnowledgeFile] = useState(null);

  const voiceInputRef = useRef(null);
  const knowledgeInputRef = useRef(null);

  const typeCards = useMemo(
    () => ['customer_support', 'lead_generation', 'appointment_scheduling'],
    []
  );

  useEffect(() => {
    let mounted = true;

    const findInList = (list = []) =>
      list.find((a) => String(a.agent_id) === String(id)) ||
      list.find((a) => String(a.id) === String(id)) ||
      list.find((a) => String(a._id) === String(id));

    const loadFromAllAgents = async () => {
      const res = await api.post('/analysis/training/agent-individual-analytics', {});
      const list = Array.isArray(res?.data?.individual_results) ? res.data.individual_results : [];
      return findInList(list);
    };

    const tryLoadFromUserAgents = async (ownerEmail) => {
      if (!ownerEmail) return null;
      try {
        const res = await api.get('/auth/admin/user-agents', { params: { email: ownerEmail } });
        const list = Array.isArray(res?.data?.agents) ? res.data.agents : [];
        return findInList(list) || null;
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401 || status === 403) return null;
        return null;
      }
    };

    const load = async () => {
      setLoading(true);
      const authedEmail = user?.email || user?.user_email || '';
      const scopedEmail = agentsScope?.type === 'user' && agentsScope?.email ? agentsScope.email : authedEmail;

      setEmail(scopedEmail);

      try {
        let base = await loadFromAllAgents();
        if (!base) base = await tryLoadFromUserAgents(scopedEmail);

        if (!base) {
          toast.error('Agent not found');
          navigate('/training', { replace: true });
          return;
        }

        const a = normalizeFromAnalytics(base);
        if (!mounted) return;

        setAgentName(a.name);
        setSelectedType(a.type || 'customer_support');
        setLanguage(a.language || 'English');
        // setModel(a.model || '');
        setSpeakingStyle(a.speakingStyle || 'professional');

        const basePrompt = cutMandatoryTail(a.prompt || '');
        setPrompt(basePrompt);

        if (!a.prompt /* || !a.model */) {
          const richer = await tryLoadFromUserAgents(scopedEmail);
          if (mounted && richer) {
            const enrichedPrompt = extractPrompt(richer);
            if (enrichedPrompt && !a.prompt) {
              setPrompt(cutMandatoryTail(enrichedPrompt));
            }

            // if (!a.model && richer.llm) {
            //   setModel(richer.llm);
            // }
          }
        }
      } catch {
        toast.error('Failed to load agent');
        navigate('/training', { replace: true });
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [id, user, agentsScope, navigate]);

  const handleVoiceFileChange = (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;

    const isMp3ByExt = /\.mp3$/i.test(file.name);
    const isMp3ByMime = (file.type || '').toLowerCase() === 'audio/mpeg';
    if (!isMp3ByExt && !isMp3ByMime) {
      toast.error('Only MP3 files are allowed for voice.');
      if (voiceInputRef.current) voiceInputRef.current.value = '';
      setVoiceFile(null);
      return;
    }

    const cloned = new File([file.slice(0, file.size, file.type)], file.name, {
      type: file.type || 'audio/mpeg',
      lastModified: Date.now(),
    });
    setVoiceFile(cloned);
  };

  const handleKnowledgeFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 1) toast.info('Only one document is allowed. Using the first one.');
    const file = files[0] || null;
    if (!file) return;

    const lower = (file.name || '').toLowerCase();
    const mime = (file.type || '').toLowerCase();
    const okExt = /\.(pdf|doc|docx)$/i.test(lower);
    const okMime = mime.includes('pdf') || mime.includes('msword') || mime.includes('officedocument');

    if (!okExt && !okMime) {
      toast.error('Only PDF or DOC/DOCX files are allowed.');
      if (knowledgeInputRef.current) knowledgeInputRef.current.value = '';
      setKnowledgeFile(null);
      return;
    }

    const cloned = new File([file.slice(0, file.size, file.type)], file.name, {
      type: file.type || 'application/octet-stream',
      lastModified: Date.now(),
    });
    setKnowledgeFile(cloned);
  };

  const clearKnowledgeFile = () => {
    setKnowledgeFile(null);
    if (knowledgeInputRef.current) knowledgeInputRef.current.value = '';
  };

  const clearVoiceFile = () => {
    setVoiceFile(null);
    if (voiceInputRef.current) voiceInputRef.current.value = '';
  };

  const handleRetrain = async () => {
    const trimmedPrompt = cutMandatoryTail(prompt);

    try {
      setSubmitting(true);

      const { success, error } = await updateAgentExact({
        email,
        agent_name: agentName,
        prompt: trimmedPrompt || undefined,
        // llm: model || undefined,
        file: knowledgeFile || undefined,
        voice_file: voiceFile || undefined,
        agent_type: selectedType || undefined,
        speaking_style: speakingStyle || undefined,
      });

      if (!success) {
        toast.error(error || 'Failed to retrain agent');
        return;
      }

      toast.success('Agent retrain submitted!');
      navigate('/training');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to retrain agent';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse card h-40" />
        <div className="animate-pulse card h-72" />
        <div className="animate-pulse card h-40" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <Link to="/training" className="mr-4 p-2.5 sm:p-2 bg-white/5 border border-white/10 rounded-xl text-white/40 hover:text-white hover:bg-white/10 transition-all group">
          <ArrowLeftIcon className="h-5 w-5 sm:h-4 w-4 group-hover:-translate-x-1 transition-transform" />
        </Link>
        <div>
          <h1 className="text-xl sm:text-[18px] font-bold text-white tracking-tight">Retrain Agent</h1>
          <p className="mt-1 sm:mt-0.5 text-sm sm:text-xs font-medium text-white/40 italic">Adjust role, behavior, voice and knowledge base.</p>
        </div>
      </div>

      <div className="controlled-glass p-6 sm:p-5 rounded-[32px] sm:rounded-2xl inner-highlight">
        <h3 className="text-lg sm:text-[15px] font-bold text-white tracking-tight mb-4 sm:mb-3">Agent Type</h3>
        <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-3">
          {typeCards.map((type) => {
            const active = selectedType === type;
            return (
              <div
                key={type}
                className={`relative rounded-lg border-2 cursor-pointer transition-colors ${active ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-gray-400'
                  }`}
                onClick={() => setSelectedType(type)}
              >
                {active && <CheckBadge />}
                <div className="p-3 sm:p-2.5">
                  <div className={`flex items-center justify-center w-10 h-10 sm:w-9 sm:h-9 mx-auto mb-3 sm:mb-2.5 rounded-lg ${getIconBgColor(type)
                    }`}>
                    <div className={getIconTextColor(type)}>
                      {getTypeIcon(type)}
                    </div>
                  </div>
                  <h4 className="text-sm sm:text-xs font-bold text-white text-center capitalize">
                    {toTitle(type)}
                  </h4>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="controlled-glass p-6 sm:p-5 rounded-[32px] sm:rounded-2xl inner-highlight">
        <h3 className="text-lg sm:text-[15px] font-bold text-white tracking-tight mb-4 sm:mb-3">Basic Settings</h3>
        <div className="grid grid-cols-1 gap-4 sm:gap-5 sm:grid-cols-2">
          <div>
            <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest pl-1 mb-2 sm:mb-1.5">Agent Name</label>
            <input
              type="text"
              value={agentName}
              disabled
              className="input opacity-70 cursor-not-allowed bg-white/5"
            />
          </div>
          <div>
            <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest pl-1 mb-2 sm:mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              disabled
              className="input opacity-70 cursor-not-allowed bg-white/5"
            />
          </div>
          {/*}
          <div>
            <label className="block text-sm font-medium text-gray-700">Default Language</label>
            <select
              className="mt-1 w-full pl-4 pr-4 sm:pl-5 sm:pr-5 py-2.5 sm:py-3 border-2 rounded-xl focus:outline-none focus:ring-4 transition border-purple-200 focus:border-purple-400 ring-purple-100"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              <option value="">Select a language</option>
              {(languageList?.languages || []).map((l) => (
                <option key={l.name} value={l.name}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
          */}

          {/*
          <div>
            <label className="block text-sm font-medium text-gray-700">AI Model</label>
            <select
              className="mt-1 w-full pl-4 pr-4 sm:pl-5 sm:pr-5 py-2.5 sm:py-3 border-2 rounded-xl focus:outline-none focus:ring-4 transition border-purple-200 focus:border-purple-400 ring-purple-100"
              value={model}
              onChange={(e) => setModel(e.target.value)}
            >
              <option value="">Select a model</option>
              {(languageList?.models || []).map((m, idx) => (
                <option key={idx} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          */}
        </div>
      </div>

      <div className="controlled-glass p-6 sm:p-5 rounded-[32px] sm:rounded-2xl inner-highlight">
        <h3 className="text-lg sm:text-[15px] font-bold text-white tracking-tight mb-4 sm:mb-3">Voice Configuration</h3>

        <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest pl-1 mb-2 sm:mb-1.5">
          Upload a voice file <span className="text-white/20 font-normal ml-1">or drag and drop</span>
        </label>
        <div className="mt-1 flex justify-center px-4 pt-4 pb-5 sm:p-4 border-2 border-white/10 border-dashed rounded-2xl bg-white/[0.02]">
          <div className="space-y-1 text-center">
            <MicrophoneIcon className="mx-auto h-10 w-10 sm:h-8 sm:w-8 text-white/20" />
            <div className="flex flex-col sm:flex-row text-sm sm:text-xs text-white/40 items-center justify-center">
              <label className="relative cursor-pointer font-bold text-primary-400 hover:text-primary-300 transition-colors">
                <span>Upload a voice file</span>
                <input
                  ref={voiceInputRef}
                  type="file"
                  accept=".mp3"
                  onClick={() => {
                    if (voiceInputRef.current) voiceInputRef.current.value = '';
                  }}
                  onChange={handleVoiceFileChange}
                  className="sr-only"
                />
              </label>
              <p className="sm:pl-1 mt-1 sm:mt-0 font-medium italic">or drag and drop</p>
            </div>
            <p className="text-xs sm:text-[10px] text-white/20 font-medium italic">MP3 only — at least 60 seconds</p>
          </div>
        </div>

        {voiceFile && (
          <div className="mt-4 sm:mt-3">
            <h4 className="text-[10px] sm:text-[9px] font-bold text-white/30 uppercase tracking-widest mb-2">File to upload</h4>
            <div className="flex items-center justify-between p-3 sm:p-2 bg-white/5 border border-white/10 rounded-xl sm:rounded-lg">
              <span className="text-sm sm:text-xs text-white/70 truncate flex-1 mr-2 font-medium">{voiceFile.name}</span>
              <button type="button" onClick={clearVoiceFile} className="text-red-400 hover:text-red-300 text-xs sm:text-[10px] font-bold uppercase tracking-widest transition-colors pl-2">
                Remove
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 sm:mt-5">
          <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest pl-1 mb-2 sm:mb-1.5">Speaking Style</label>
          <select
            className="input sm:py-2.5"
            value={speakingStyle}
            onChange={(e) => setSpeakingStyle(e.target.value)}
          >
            <option value="professional">Professional</option>
            <option value="friendly">Friendly</option>
            <option value="casual">Casual</option>
            <option value="energetic">Energetic</option>
          </select>
        </div>
      </div>

      <div className="controlled-glass p-6 sm:p-5 rounded-[32px] sm:rounded-2xl inner-highlight">
        <h3 className="text-lg sm:text-[15px] font-bold text-white tracking-tight mb-2">Knowledge Base</h3>
        <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest pl-1 mb-2 sm:mb-1.5">Upload Document</label>
        <div className="mt-1 flex justify-center px-4 pt-4 pb-5 sm:p-4 border-2 border-white/10 border-dashed rounded-2xl bg-white/[0.02]">
          <div className="space-y-1 text-center">
            <DocumentArrowUpIcon className="mx-auto h-10 w-10 sm:h-8 sm:w-8 text-white/20" />
            <div className="flex flex-col sm:flex-row text-sm sm:text-xs text-white/40 items-center justify-center">
              <label className="relative cursor-pointer font-bold text-primary-400 hover:text-primary-300 transition-colors">
                <span>Upload document</span>
                <input
                  ref={knowledgeInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onClick={() => {
                    if (knowledgeInputRef.current) knowledgeInputRef.current.value = '';
                  }}
                  onChange={handleKnowledgeFileChange}
                  className="sr-only"
                />
              </label>
              <p className="sm:pl-1 mt-1 sm:mt-0 font-medium italic">or drag and drop</p>
            </div>
            <p className="text-xs sm:text-[10px] text-white/20 font-medium italic">DOCX or PDF only</p>
          </div>
        </div>

        {knowledgeFile && (
          <div className="mt-4 sm:mt-3">
            <h4 className="text-[10px] sm:text-[9px] font-bold text-white/30 uppercase tracking-widest mb-2">File to upload</h4>
            <div className="flex items-center justify-between p-3 sm:p-2 bg-white/5 border border-white/10 rounded-xl sm:rounded-lg">
              <span className="text-sm sm:text-xs text-white/70 truncate flex-1 mr-2 font-medium">{knowledgeFile.name}</span>
              <button
                type="button"
                onClick={clearKnowledgeFile}
                className="text-red-400 hover:text-red-300 text-xs sm:text-[10px] font-bold uppercase tracking-widest transition-colors pl-2"
              >
                Remove
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="controlled-glass p-6 sm:p-5 rounded-[32px] sm:rounded-2xl inner-highlight">
        <h3 className="text-lg sm:text-[15px] font-bold text-white tracking-tight">Prompt Message</h3>
        <p className="mt-1 text-sm sm:text-xs font-medium text-white/40 italic">
          Describe how your agent should behave and respond.
        </p>

        <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest pl-1 mt-4 sm:mt-3 mb-2 sm:mb-1.5">Initial Prompt</label>
        <textarea
          rows={5}
          className="input sm:py-3"
          placeholder="e.g., Respond politely and help users with booking appointments."
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
      </div>

      <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 sm:gap-4 rounded-xl pb-20 sm:pb-10 pt-4">
        <Link
          to="/training"
          className="btn btn-secondary w-full sm:w-auto sm:px-10"
        >
          Cancel
        </Link>
        <button
          type="button"
          onClick={handleRetrain}
          disabled={submitting}
          className="btn btn-primary w-full sm:w-auto sm:px-12 flex items-center justify-center"
        >
          <SparklesIcon className="h-4 w-4 mr-2" />
          {submitting ? 'Saving…' : 'Retrain Agent'}
        </button>
      </div>
    </div>
  );
};

export default AgentRetrain;